import { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useClerkAuth } from '../hooks/use-clerk-auth';
import apiClient from '../services/api-client';

const TourContext = createContext();

export const TourProvider = ({ children }) => {
  const { user, isSignedIn, getSessionToken, loading, initDone } = useClerkAuth();
  const location = useLocation();

  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Tour state
  const [isTourActive, setIsTourActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [hasCompletedTour, setHasCompletedTour] = useState(() => {
    const saved = localStorage.getItem('ocean_tour_completed');
    return saved ? JSON.parse(saved) : false;
  });
  const [tourDismissed, setTourDismissed] = useState(() => {
    const saved = localStorage.getItem('ocean_tour_dismissed');
    return saved ? JSON.parse(saved) : false;
  });
  const [backendTourStatus, setBackendTourStatus] = useState(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);

  // Fetch tour status from backend on mount
  useEffect(() => {
    let isMounted = true;

    const fetchTourStatus = async () => {
      // Wait for Clerk auth to be fully initialized before making API calls
      if (!initDone || loading) {
        return;
      }

      // Don't fetch if user is not signed in
      if (!isSignedIn || !user) {
        return;
      }

      // Verify getSessionToken is a function before proceeding
      if (typeof getSessionToken !== 'function') {
        console.warn('getSessionToken not ready, skipping tour status fetch');
        return;
      }

      // Small delay to ensure Clerk session is fully established
      await new Promise(resolve => setTimeout(resolve, 300));

      // Check if component is still mounted after delay
      if (!isMounted) return;

      setIsCheckingStatus(true);
      try {
        // Test that we can actually get a token before making the API call
        const token = await getSessionToken();
        if (!token) {
          console.log('No token available, skipping tour status fetch');
          setIsCheckingStatus(false);
          return;
        }

        // Only make API call if we have a valid token
        const response = await apiClient.get('/settings/tour-status', {}, getSessionToken);

        if (response && response.data) {
          setBackendTourStatus(response.data);

          // Sync local storage with backend
          const backendCompleted = response.data.has_completed;
          const backendDismissed = response.data.dismissed;

          setHasCompletedTour(backendCompleted);
          setTourDismissed(backendDismissed);

          localStorage.setItem('ocean_tour_completed', JSON.stringify(backendCompleted));
          localStorage.setItem('ocean_tour_dismissed', JSON.stringify(backendDismissed));
        }
      } catch (error) {
        console.error('Failed to fetch tour status:', error);
        // Don't throw - just log the error and continue
      } finally {
        if (isMounted) {
          setIsCheckingStatus(false);
        }
      }
    };

    fetchTourStatus();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, user, initDone, loading]);

  // Auto-trigger tour on /upload page for first-time users
  useEffect(() => {
    // Don't even schedule if basic conditions aren't met
    if (!initDone || loading || !isSignedIn || isTourActive || isCheckingStatus) {
      return;
    }

    // Only check route, completed/dismissed status for scheduling
    if (location.pathname !== '/upload' || hasCompletedTour || tourDismissed) {
      return;
    }

    console.log('📅 Scheduling tour auto-start check in 1 second...');

    // Schedule a check - this will run even if isCheckingStatus changes in the meantime
    const timer = setTimeout(() => {
      // Do final check at the moment we're about to start
      console.log('🔍 Final tour start check:', {
        initDone,
        loading,
        isSignedIn,
        isCheckingStatus,
        isTourActive,
        hasCompletedTour,
        tourDismissed,
        pathname: location.pathname
      });

      // Verify all conditions one more time
      if (!initDone || loading || !isSignedIn || isCheckingStatus || isTourActive ||
          hasCompletedTour || tourDismissed || location.pathname !== '/upload') {
        console.log('❌ Tour start cancelled - conditions changed');
        return;
      }

      console.log('🎯 Starting tour now!');
      startTour();
    }, 1000);

    return () => {
      console.log('🧹 Cleaning up tour timer');
      clearTimeout(timer);
    };
  }, [location.pathname, isSignedIn, hasCompletedTour, tourDismissed, isTourActive, initDone, loading, isCheckingStatus]);

  // Start tour
  const startTour = () => {
    setIsTourActive(true);
    setCurrentStepIndex(0);
  };

  // Stop tour (pause)
  const stopTour = () => {
    setIsTourActive(false);
  };

  // Skip tour (dismiss permanently)
  const skipTour = async () => {
    setIsTourActive(false);
    setTourDismissed(true);
    localStorage.setItem('ocean_tour_dismissed', JSON.stringify(true));

    // Update backend if auth is ready
    if (typeof getSessionToken === 'function') {
      try {
        await apiClient.post('/settings/tour-dismiss', {}, getSessionToken);
      } catch (error) {
        console.error('Failed to update tour status:', error);
      }
    }
  };

  // Mark tour as completed
  const markTourCompleted = async () => {
    setIsTourActive(false);
    setHasCompletedTour(true);
    localStorage.setItem('ocean_tour_completed', JSON.stringify(true));

    // Update backend if auth is ready
    if (typeof getSessionToken === 'function') {
      try {
        await apiClient.post('/settings/tour-complete', {}, getSessionToken);
      } catch (error) {
        console.error('Failed to mark tour as completed:', error);
      }
    }
  };

  // Reset tour (for replay)
  const resetTour = async () => {
    setHasCompletedTour(false);
    setTourDismissed(false);
    setCurrentStepIndex(0);
    localStorage.setItem('ocean_tour_completed', JSON.stringify(false));
    localStorage.setItem('ocean_tour_dismissed', JSON.stringify(false));

    // Update backend if auth is ready
    if (typeof getSessionToken === 'function') {
      try {
        await apiClient.post('/settings/tour-reset', {}, getSessionToken);
      } catch (error) {
        console.error('Failed to reset tour:', error);
      }
    }
  };

  // Navigate to next step
  const nextStep = () => {
    setCurrentStepIndex(prev => prev + 1);
  };

  // Navigate to previous step
  const prevStep = () => {
    setCurrentStepIndex(prev => Math.max(0, prev - 1));
  };

  const value = {
    isTourActive,
    currentStepIndex,
    hasCompletedTour,
    tourDismissed,
    backendTourStatus,
    isCheckingStatus,
    isMobile,
    startTour,
    stopTour,
    skipTour,
    markTourCompleted,
    resetTour,
    nextStep,
    prevStep,
    setCurrentStepIndex
  };

  return (
    <TourContext.Provider value={value}>
      {children}
    </TourContext.Provider>
  );
};

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};
